import { AxDataTable } from "@/components/ax/dataTable/AxDataTable";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { ROUTES } from "@/constants/routeConstants";
import { MenuHeader } from "@/lib/types";
import { getMenuHeader, updateMenuHeader } from "@/sideEffects/api";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { UpdateRowButton } from "@/components/ax/dataTable/UpdateRowButton";
import { AddRowButton } from "@/components/ax/dataTable/AddRowButton";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { showSuccess, showApiError } from "@/lib/toast";

export const MenuHeaderPage = () => {
  const { t } = useTranslation();

  useBreadcrumbs([{ name: t("entities.menuHeader"), path: ROUTES.MENU_HEADER }]);

  const { goToMenuHeaderDetail, goToMenuHeaderAdd } = useAppNavigate();

  const columns: ColumnDef<MenuHeader>[] = [
    {
      accessorKey: "name",
      header: t("common.name"),
    },
    {
      accessorKey: "order",
      header: t("common.order"),
    },
    {
      accessorKey: "id",
      header: () => "",
      cell: ({ row }) => {
        return (
          <div className="flex items-center justify-end gap-2">
            <UpdateRowButton onClickUpdate={() => goToMenuHeaderDetail(row.original.id)} />
          </div>
        );
      },
    },
  ];

  const queryClient = useQueryClient();
  const updateOrdersMutation = useMutation({
    mutationFn: async (newData: MenuHeader[]) => {
      const currentData = (await getMenuHeader({ numberOfObjectsPerPage: 1000, pageNumber: 1 })).data.result || [];

      // Create maps for easy lookup
      const currentOrderMap = new Map(currentData.map((item) => [item.id, Number(item.order)]));

      // Calculate which items actually need updates
      const itemsToUpdate = newData.filter((item, index) => {
        const newOrder = (index + 1) * 100;
        const currentOrder = currentOrderMap.get(item.id);
        return currentOrder !== newOrder;
      });

      // Only update items that changed position
      const updatePromises = itemsToUpdate.map((menu) => {
        const newOrder = (newData.findIndex((item) => item.id === menu.id) + 1) * 100;
        return updateMenuHeader(menu.id, menu.name, newOrder);
      });

      return Promise.all(updatePromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getMenuHeader"],
      });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  const clientSideSort = (data: MenuHeader[]) => {
    return data.sort((a, b) => a.order - b.order);
  };

  return (
    <AxDataTable
      columns={columns}
      fetcher={getMenuHeader}
      numberOfObjectsPerPage={1000}
      hidePageSizeSelector
      getRowId={(row) => row.id.toString()}
      uniqQueryKey="getMenuHeader"
      onReorder={(newData) => {
        updateOrdersMutation.mutate(newData);
      }}
      clientSideSort={clientSideSort}
      extraHeader={
        <>
          <AddRowButton
            title={t("common.addNewEntity", { entity: t("entities.menuHeader") })}
            onClickAdd={goToMenuHeaderAdd}
          />
        </>
      }
    />
  );
};
